<template>
    <!-- This <span> element is necessary to determine bounding client rectangle for lazy loading -->
    <span>
        <Transition v-if="isVisible" appear>
            <figure
                v-bind:class="{
                    [$style.cropContainer]: isCroppedAndCentered,
                }"
                v-bind:style="{
                    'max-width': cropWidth,
                    'max-height': cropHeight,
                }"
            >
                <picture v-bind:class="$style.image">
                    <source v-if="hasWebp" v-bind:srcset="imageSlugPath + '.webp'" type="image/webp">
                    <img
                        v-bind:src="imagePath"
                        v-bind:alt="alt"
                        v-bind:style="{
                            height,
                            width,
                        }"
                        v-bind="$attrs"
                    >
                </picture>
            </figure>
        </Transition>
    </span>
</template>

<script>
    import CdnContainers from '@constants/cdnContainers';

    export default {
        /**
         * Determine how unrecognized component properties should be handled by the Vue compiler.
         *
         * @link https://vuejs.org/v2/api/#inheritAttrs
         *
         * @type {Boolean}
         */
        inheritAttrs: false,

        /**
         * Define the properties passed into the component from the parent.
         *
         * @link https://vuejs.org/v2/guide/components.html#Props
         */
        props: {
            image: {
                type: String,
                required: true,
            },
            alt: {
                type: String,
                default: '',
            },
            width: {
                type: String,
                default: null,
            },
            height: {
                type: String,
                default: null,
            },
            cdnContainer: {
                type: String,
                default: null,
            },
            hasWebp: {
                type: Boolean,
                default: false,
            },
            isLazyLoaded: {
                type: Boolean,
                default: true,
            },
            cropWidth: {
                type: String,
                default: 'auto',
            },
            cropHeight: {
                type: String,
                default: 'auto',
            },
        },

        /**
         * You must define defaults for all Vue models and reactive Vue template data.
         *
         * @link https://vuejs.org/v2/api/#data
         *
         * @return Object Vue model and binding defaults.
         */
        data () {
            return {
                isVisible: false,
            };
        },

        /**
         * Cached computed methods for complex logic on the view layer.
         *
         * @link https://vuejs.org/v2/guide/computed.html
         *
         * @type Object The computed functions
         */
        computed: {
            /**
             * Get the image filename slug.
             *
             * @return {String}
             */
            imageSlugPath () {
                return this.imagePath.replace(/(\.(png|jpg|jpeg|gif))?/gi, '');
            },

            /**
             * Rewrite image path if CDN container is specified
             *
             * @return {String}
             */
            imagePath () {
                return (this.cdnContainer ? CdnContainers[this.cdnContainer] : '') + this.image;
            },

            isCroppedAndCentered () {
                return this.cropWidth !== 'auto' || this.cropHeight !== 'auto';
            },
        },

        /**
         * This method logic will be run immediately after the Vue instance is created.
         *
         * @link https://vuejs.org/v2/guide/instance.html#Lifecycle-Diagram
         */
        created () {
            this.lazyLoadEventTypes = [
                'scroll',
                'touchmove',
            ];
        },

        /**
         * This method logic will be run immediately after the Vue instance is mounted.
         *
         * @link https://vuejs.org/v2/guide/instance.html#Lifecycle-Diagram
         */
        mounted () {
            // Determine if the image should be lazy loaded
            if (! this.isLazyLoaded) {
                // Load image immediately and abort scrolling event listeners
                return this.isVisible = true;
            }

            // Lazy load images on mount if visible within viewport
            this.lazyLoadImage();

            // Attach scrolling event listers to lazy load images
            this.lazyLoadEventTypes.forEach(type => window.document.addEventListener(type, this.lazyLoadImage, true));
        },

        /**
         * Lifecycle hook that is run immediately before the Vue component instance is destroyed.
         *
         * @link https://vuejs.org/v2/api/#beforeDestroy
         */
        beforeDestroy () {
            // Remove event listeners immediately before component is destroyed
            this.lazyLoadEventTypes.forEach(type => window.document.removeEventListener(type, this.lazyLoadImage, true));
        },

        /**
         * Non-cached methods that are used by the Vue instance and template.
         *
         * @type Object ES6 methods
         */
        methods: {
            /**
             * Will determine if the image is visible within the view port and begin lazily fetching the image.
             *
             * @return void
             */
            lazyLoadImage () {
                // Don't reload or hide images that are already loaded
                if (this.isVisible) {
                    return false;
                }

                // Get the bounding parameters of this component
                const elementBounds = this.$el.getBoundingClientRect();

                // Define the top and bottom pixel location of the view port
                const viewportTop = window.pageYOffset;
                const viewportBottom = viewportTop + document.documentElement.clientHeight;

                // Define the top and bottom pixel location of the component
                const top = elementBounds.top + viewportTop - document.documentElement.clientTop;
                const bottom = top + elementBounds.height;

                // Flag if the image is currently visible within the view port to begin lazily fetching the image
                this.isVisible = bottom > viewportTop && top < viewportBottom;
            },
        },
    };
</script>

<style lang="scss" module>
    .image {
        flex-shrink: 0;
        width: 100%;

        &:global(.v-enter-active) {
            transition: opacity .5s;
        }

        &:global(.v-enter) {
            @apply opacity-0;
        }
    }

    .cropContainer {
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
    }
</style>
